import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Header } from "components/DesignSystem/Header/Header";
import { AddJournalEntryModal } from "components/JournalEntry/AddJournalEntryModal";
import { BulkJournalEntryUpload } from "components/JournalEntry/BulkJournalEntryUpload";
import { ExportJournalEntry } from "components/JournalEntry/ExportJournalEntry";
import { JournalEntry } from "components/JournalEntry/JournalEntry";
import { TakeConfirmationModal } from "components/TakeConfirmationModal/TakeConfirmationModal";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { ArrowDown } from "components/icons/ArrowDown";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { MultipleFiles } from "components/icons/MultipleFiles";
import { PlusIcon } from "components/icons/PlusIcon";
import { useModal } from "hooks/useModal";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedUi } from "components/LockedUi/LockedUi";

const JournalEntryActions = ({
  commentPaneToggle,
}: {
  commentPaneToggle: () => void;
}) => {
  const addJournalEntryModal = useModal();
  const openBulkUpload = useModal();

  return (
    <div className="t-flex t-gap-2 t-items-center">
      <ExportJournalEntry />
      <Dropdown.Root>
        <Dropdown.Trigger asChild className="t-group t-text-text-60">
          <div>
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
              blockedUi={
                <LockedButton text="Add journal entry" size="xsmall" />
              }
            >
              <Button
                size="xsmall"
                customType="primary"
                aria-label="Add journal entry"
              >
                <span className="sm:t-hidden">Add journal entry</span>
                <span className="t-hidden sm:t-inline">Add</span>
                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
                  <ArrowDown color="currentColor" strokeWidth="1.5" />
                </div>
              </Button>
            </LockedUi>
          </div>
        </Dropdown.Trigger>

        <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
          <Dropdown.Portal>
            <Dropdown.Content
              sideOffset={8}
              side="bottom"
              className="t-min-w-48"
              align="end"
            >
              <Dropdown.Item onSelect={addJournalEntryModal.open}>
                <div className="t-flex t-items-center t-gap-1">
                  <PlusIcon size="12" />
                  <div>Create a Journal entry</div>
                </div>
              </Dropdown.Item>

              <Dropdown.Item onSelect={openBulkUpload.open}>
                <div className="t-flex t-items-center t-gap-1">
                  <MultipleFiles />
                  <div>Add Journal entry in bulk</div>
                </div>
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </LockedUi>
      </Dropdown.Root>
      <Button customType="icon" onClick={commentPaneToggle} size="xsmall">
        <ChatTeardropDots />
      </Button>
      {addJournalEntryModal.isOpen && (
        <>
          <AddJournalEntryModal
            close={addJournalEntryModal.close}
            isOpen={addJournalEntryModal.isOpen}
          />
          <TakeConfirmationModal
            isOpen={addJournalEntryModal.showConfirmation}
            dismiss={addJournalEntryModal.dismissConfirmation}
            close={addJournalEntryModal.close}
          />
        </>
      )}
      {openBulkUpload.isOpen && <BulkJournalEntryUpload {...openBulkUpload} />}
    </div>
  );
};

export const JournalEntries = () => {
  const commentPane = useModal();

  return (
    <DashboardContainer className="t-h-full sm:t-h-auto sm:t-overflow-x-visible">
      <DashboardContainer.Header className="t-px-10 t-z-header sm:t-px-3">
        <Header
          v2
          title="Journal Entries"
          right={<JournalEntryActions commentPaneToggle={commentPane.toggle} />}
        />
      </DashboardContainer.Header>

      <DashboardContainer.Content className="sm:t-overflow-visible">
        <div className="t-h-full t-w-full">
          <JournalEntry commentPaneControl={commentPane} />
        </div>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
